import React, {createContext, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {onAuthStateChanged} from 'firebase/auth';
import {firebaseAuth} from '../services/firebase.service';
import {getAccessToken} from '../services/auth.service';
import {MeDto, PermissionCodeType} from './permissions';
import {logError} from '../services/logger.service';

interface PermissionContextValue {
  me: MeDto | null;
  loading: boolean;
  hasPermission: (code: PermissionCodeType | string) => boolean;
  hasAnyPermission: (...codes: Array<PermissionCodeType | string>) => boolean;
  refresh: () => Promise<void>;
}

const PermissionContext = createContext<PermissionContextValue | undefined>(undefined);

async function fetchMe(): Promise<MeDto> {
  const token = await getAccessToken();
  if (!token) {
    throw new Error('Not authenticated');
  }
  const response = await fetch(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/users/me`,
    {headers: {Authorization: `Bearer ${token}`}},
  );
  if (!response.ok) {
    throw new Error(`Failed to load current user (${response.status})`);
  }
  return await response.json() as MeDto;
}

export const PermissionProvider: React.FC<{children: React.ReactNode}> = ({children}) => {
  const [me, setMe] = useState<MeDto | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = await getAccessToken();
    if (!token) {
      setMe(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      setMe(await fetchMe());
    } catch (error) {
      logError('Failed to load permissions', {}, error as Error);
      setMe(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(firebaseAuth, (user) => {
      if (!user) {
        setMe(null);
        setLoading(false);
        return;
      }
      void refresh();
    });
    return unsubscribe;
  }, [refresh]);

  const permissionSet = useMemo(() => new Set(me?.permissions ?? []), [me]);

  const hasPermission = useCallback(
    (code: PermissionCodeType | string) => me?.isAdmin === true || permissionSet.has(code),
    [me, permissionSet],
  );

  const hasAnyPermission = useCallback(
    (...codes: Array<PermissionCodeType | string>) =>
      me?.isAdmin === true || codes.some(code => permissionSet.has(code)),
    [me, permissionSet],
  );

  const value = useMemo(
    () => ({me, loading, hasPermission, hasAnyPermission, refresh}),
    [me, loading, hasPermission, hasAnyPermission, refresh],
  );

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
};

export const usePermissions = (): PermissionContextValue => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

const unauthenticatedPermissions: PermissionContextValue = {
  me: null,
  loading: false,
  hasPermission: () => false,
  hasAnyPermission: () => false,
  refresh: async () => {},
};

/** Safe on customer-facing routes that also render inside the authenticated layout. */
export const useOptionalPermissions = (): PermissionContextValue => {
  return useContext(PermissionContext) ?? unauthenticatedPermissions;
};
